import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { transactionsService, categoriesService } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  TrendingUp, 
  TrendingDown, 
  Wallet, 
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import { format, parseISO, startOfMonth, endOfMonth, isWithinInterval } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';

const Dashboard = () => {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [transData, catData] = await Promise.all([
        transactionsService.getAll(),
        categoriesService.getAll()
      ]);
      setTransactions(transData || []);
      setCategories(catData || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Calculate summary data
  const summary = useMemo(() => {
    const totalReceitas = transactions
      .filter(t => t.tipo?.toLowerCase() === 'receita')
      .reduce((sum, t) => sum + (t.valor || 0), 0);
    
    const totalDespesas = transactions
      .filter(t => t.tipo?.toLowerCase() === 'despesa')
      .reduce((sum, t) => sum + (t.valor || 0), 0);
    
    const saldo = totalReceitas - totalDespesas;
    
    return { totalReceitas, totalDespesas, saldo };
  }, [transactions]);

  // Monthly data for bar chart
  const monthlyData = useMemo(() => {
    const months = {};
    
    transactions.forEach(t => {
      if (!t.data) return;
      const date = parseISO(t.data);
      const monthKey = format(date, 'MMM', { locale: ptBR });
      
      if (!months[monthKey]) {
        months[monthKey] = { name: monthKey, receitas: 0, despesas: 0 };
      }
      
      if (t.tipo?.toLowerCase() === 'receita') {
        months[monthKey].receitas += t.valor || 0;
      } else {
        months[monthKey].despesas += t.valor || 0;
      }
    });
    
    return Object.values(months).slice(-6);
  }, [transactions]);

  // Category data for pie chart
  const categoryData = useMemo(() => {
    const catTotals = {};
    
    transactions.forEach(t => {
      const cat = categories.find(c => c.id === t.categoriaId);
      const catName = cat?.nome || 'Sem categoria';
      
      if (!catTotals[catName]) {
        catTotals[catName] = 0;
      }
      catTotals[catName] += t.valor || 0;
    });
    
    return Object.entries(catTotals)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }, [transactions, categories]);

  // Recent transactions
  const recentTransactions = useMemo(() => {
    return [...transactions]
      .sort((a, b) => new Date(b.data) - new Date(a.data))
      .slice(0, 5);
  }, [transactions]);

  const COLORS = ['#22c55e', '#3b82f6', '#eab308', '#ef4444', '#8b5cf6'];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-3 shadow-lg">
          <p className="text-white font-medium mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="dashboard-loading">
        <RefreshCw className="w-8 h-8 text-green-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-white font-['Manrope']">
            Olá, {user?.nome?.split(' ')[0] || 'Usuário'}!
          </h1>
          <p className="text-zinc-400 mt-1">
            Aqui está o resumo das suas finanças
          </p>
        </div>
        <Button
          onClick={fetchData}
          variant="outline"
          className="border-zinc-700 text-zinc-300 hover:border-green-500 hover:text-green-400"
          data-testid="refresh-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Saldo Card */}
        <Card className="bg-zinc-900/50 border border-white/10 rounded-xl" data-testid="saldo-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-zinc-400 text-sm uppercase tracking-wider">Saldo Atual</span>
              <div className="p-2 rounded-lg bg-green-500/10">
                <Wallet className="w-5 h-5 text-green-400" />
              </div>
            </div>
            <p className={`text-3xl font-bold font-mono ${summary.saldo >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {formatCurrency(summary.saldo)}
            </p>
            <div className="flex items-center gap-1 mt-2">
              {summary.saldo >= 0 ? (
                <ArrowUpRight className="w-4 h-4 text-green-400" />
              ) : (
                <ArrowDownRight className="w-4 h-4 text-red-400" />
              )}
              <span className={`text-sm ${summary.saldo >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {summary.saldo >= 0 ? 'Positivo' : 'Negativo'}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Receitas Card */}
        <Card className="bg-zinc-900/50 border border-white/10 rounded-xl" data-testid="receitas-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-zinc-400 text-sm uppercase tracking-wider">Total Receitas</span>
              <div className="p-2 rounded-lg bg-green-500/10">
                <TrendingUp className="w-5 h-5 text-green-400" />
              </div>
            </div>
            <p className="text-3xl font-bold font-mono text-green-400">
              {formatCurrency(summary.totalReceitas)}
            </p>
            <p className="text-sm text-zinc-500 mt-2">
              {transactions.filter(t => t.tipo?.toLowerCase() === 'receita').length} lançamentos
            </p>
          </CardContent>
        </Card>

        {/* Despesas Card */}
        <Card className="bg-zinc-900/50 border border-white/10 rounded-xl" data-testid="despesas-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-zinc-400 text-sm uppercase tracking-wider">Total Despesas</span>
              <div className="p-2 rounded-lg bg-red-500/10">
                <TrendingDown className="w-5 h-5 text-red-400" />
              </div>
            </div>
            <p className="text-3xl font-bold font-mono text-red-400">
              {formatCurrency(summary.totalDespesas)}
            </p>
            <p className="text-sm text-zinc-500 mt-2">
              {transactions.filter(t => t.tipo?.toLowerCase() === 'despesa').length} lançamentos
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Bar Chart */}
        <Card className="bg-zinc-900/50 border border-white/10 rounded-xl" data-testid="bar-chart-card">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2 font-['Manrope']">
              <BarChart3 className="w-5 h-5 text-green-400" />
              Receitas vs Despesas
            </CardTitle>
          </CardHeader>
          <CardContent>
            {monthlyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="name" stroke="#a1a1aa" />
                  <YAxis stroke="#a1a1aa" tickFormatter={(v) => `R$${v}`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="receitas" name="Receitas" fill="#22c55e" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="despesas" name="Despesas" fill="#ef4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center h-[300px] text-zinc-500">
                <BarChart3 className="w-12 h-12 mb-4 opacity-50" />
                <p>Nenhum dado para exibir</p>
                <p className="text-sm">Adicione lançamentos para ver o gráfico</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pie Chart */}
        <Card className="bg-zinc-900/50 border border-white/10 rounded-xl" data-testid="pie-chart-card">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2 font-['Manrope']">
              <Calendar className="w-5 h-5 text-green-400" />
              Gastos por Categoria
            </CardTitle>
          </CardHeader>
          <CardContent>
            {categoryData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{ backgroundColor: '#18181b', border: '1px solid #3f3f46' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center h-[300px] text-zinc-500">
                <Calendar className="w-12 h-12 mb-4 opacity-50" />
                <p>Nenhum dado para exibir</p>
                <p className="text-sm">Adicione lançamentos para ver o gráfico</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Transactions */}
      <Card className="bg-zinc-900/50 border border-white/10 rounded-xl" data-testid="recent-transactions-card">
        <CardHeader>
          <CardTitle className="text-white font-['Manrope']">Lançamentos Recentes</CardTitle>
        </CardHeader>
        <CardContent>
          {recentTransactions.length > 0 ? (
            <div className="space-y-4">
              {recentTransactions.map((transaction) => {
                const category = categories.find(c => c.id === transaction.categoriaId);
                const isReceita = transaction.tipo?.toLowerCase() === 'receita';
                
                return (
                  <div 
                    key={transaction.id}
                    className="flex items-center justify-between p-4 rounded-xl bg-black/20 border border-white/5 hover:border-white/10 transition-colors"
                    data-testid={`transaction-item-${transaction.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${isReceita ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                        {isReceita ? (
                          <TrendingUp className="w-5 h-5 text-green-400" />
                        ) : (
                          <TrendingDown className="w-5 h-5 text-red-400" />
                        )}
                      </div>
                      <div>
                        <p className="text-white font-medium">
                          {category?.nome || 'Sem categoria'}
                        </p>
                        <p className="text-sm text-zinc-500">
                          {transaction.data ? format(parseISO(transaction.data), 'dd/MM/yyyy', { locale: ptBR }) : '-'}
                        </p>
                      </div>
                    </div>
                    <p className={`text-lg font-mono font-bold ${isReceita ? 'text-green-400' : 'text-red-400'}`}>
                      {isReceita ? '+' : '-'}{formatCurrency(transaction.valor)}
                    </p>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-zinc-500">
              <p>Nenhum lançamento encontrado</p>
              <p className="text-sm mt-1">Adicione seu primeiro lançamento</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
